import type { Locale, TranslationKey } from "./i18n";

type CompletionTranslationKey = Extract<TranslationKey,
  | "accountSignInUnavailable"
  | "accountSignInUnavailableHint"
  | "downloadOnAppStore"
  | "emptyResultsTitle"
  | "methodology"
  | "periodClampedByPlan"
  | "loadingBacktestTitle"
  | "loadingBacktestData"
  | "loadingBacktestEngine"
  | "loadingBacktestReport"
  | "loadingAnalysisTitle"
  | "loadingAnalysisData"
  | "loadingAnalysisEngine"
  | "loadingAnalysisReport"
  | "estimatedWait"
  | "privacyTermsTitle"
  | "privacyTermsBody"
  | "privacyTermsLink"
  | "webSubscriptionTerms"
  | "sample"
  | "confidenceHigh"
  | "confidenceMedium"
  | "confidenceLow"
  | "confidenceLargeTradeSample"
  | "confidenceModerateTradeSample"
  | "confidenceSmallTradeSample"
  | "confidenceVerySmallTradeSample"
  | "confidenceWideCandleSample"
  | "confidenceShortCandleHistory"
  | "confidenceNoGrossLoss"
  | "confidenceNoClosedTrades"
  | "confidenceStrongProfitFactor"
  | "confidencePositiveProfitFactor"
  | "confidenceWeakProfitFactor"
  | "confidencePositiveNetProfit"
  | "confidenceNegativeNetProfit"
  | "confidenceHighWinRate"
  | "confidenceLowWinRate"
  | "confidenceControlledDrawdown"
  | "confidenceHighDrawdown"
  | "confidenceModerateDrawdown"
  | "confidenceConcentratedProfit"
  | "viewHistory"
  | "emailAlreadyInUse"
  | "invalidCredentials"
  | "passwordTooWeak"
  | "signInUnavailable"
>;

type CompletionStrings = Record<CompletionTranslationKey, string>;
type CompletionLocale = Exclude<Locale, "en" | "vi">;

const completeTranslations = {
  hi: {
    accountSignInUnavailable: "खाता साइन-इन उपलब्ध नहीं है",
    accountSignInUnavailableHint: "बैकटेस्ट अभी भी चल सकते हैं। खाता सिंक अस्थायी रूप से उपलब्ध नहीं है।",
    downloadOnAppStore: "App Store से डाउनलोड करें",
    emptyResultsTitle: "अभी कोई बैकटेस्ट परिणाम नहीं है",
    methodology: "कार्यप्रणाली",
    periodClampedByPlan: "अवधि को आपके प्लान की सीमा के अनुसार समायोजित किया गया",
    loadingBacktestTitle: "बाज़ार सिमुलेशन चल रहा है",
    loadingBacktestData: "कैंडल डेटा लोड हो रहा है",
    loadingBacktestEngine: "रणनीति के नियम जाँचे जा रहे हैं",
    loadingBacktestReport: "चार्ट और ट्रेड रिपोर्ट तैयार हो रही है",
    loadingAnalysisTitle: "ट्रेड व्यवहार की समीक्षा हो रही है",
    loadingAnalysisData: "बंद ट्रेड पढ़े जा रहे हैं",
    loadingAnalysisEngine: "कमज़ोरियाँ खोजी जा रही हैं",
    loadingAnalysisReport: "बेहतर प्रॉम्प्ट तैयार हो रहा है",
    estimatedWait: "अनुमानित प्रतीक्षा",
    privacyTermsTitle: "उपयोग की शर्तें",
    privacyTermsBody: "सदस्यताएँ iOS ऐप में खरीदी जाती हैं और Apple की मानक उपयोग शर्तों के अधीन हैं। खरीद से पहले Apple अंतिम स्थानीय मूल्य और नवीनीकरण शर्तें दिखाता है।",
    privacyTermsLink: "Apple उपयोग की शर्तें (EULA)",
    webSubscriptionTerms: "वेब सदस्यता की शर्तें",
    sample: "नमूना",
    confidenceHigh: "उच्च",
    confidenceMedium: "मध्यम",
    confidenceLow: "कम",
    confidenceLargeTradeSample: "बड़ा ट्रेड नमूना",
    confidenceModerateTradeSample: "मध्यम ट्रेड नमूना",
    confidenceSmallTradeSample: "छोटा लेकिन उपयोगी ट्रेड नमूना",
    confidenceVerySmallTradeSample: "बहुत छोटा ट्रेड नमूना",
    confidenceWideCandleSample: "व्यापक कैंडल नमूना",
    confidenceShortCandleHistory: "कम अवधि का कैंडल इतिहास",
    confidenceNoGrossLoss: "इस नमूने में कोई सकल घाटे वाला ट्रेड नहीं",
    confidenceNoClosedTrades: "कोई बंद ट्रेड नहीं",
    confidenceStrongProfitFactor: "मज़बूत प्रॉफिट फैक्टर",
    confidencePositiveProfitFactor: "सकारात्मक प्रॉफिट फैक्टर",
    confidenceWeakProfitFactor: "प्रॉफिट फैक्टर 1.0 से कम",
    confidencePositiveNetProfit: "लागत के बाद शुद्ध लाभ सकारात्मक",
    confidenceNegativeNetProfit: "लागत के बाद शुद्ध लाभ नकारात्मक",
    confidenceHighWinRate: "जीत दर 60% से अधिक",
    confidenceLowWinRate: "जीत दर 40% से कम",
    confidenceControlledDrawdown: "नियंत्रित ड्रॉडाउन",
    confidenceHighDrawdown: "उच्च ड्रॉडाउन",
    confidenceModerateDrawdown: "मध्यम ड्रॉडाउन",
    confidenceConcentratedProfit: "लाभ एक बड़े विजेता पर बहुत अधिक निर्भर है",
    viewHistory: "इतिहास देखें",
    emailAlreadyInUse: "इस ईमेल से पहले से एक खाता मौजूद है।",
    invalidCredentials: "ईमेल या पासवर्ड गलत है।",
    passwordTooWeak: "पासवर्ड कम से कम 8 वर्ण का होना चाहिए।",
    signInUnavailable: "साइन-इन अस्थायी रूप से उपलब्ध नहीं है। कृपया बाद में फिर प्रयास करें।",
  },
  ur: {
    accountSignInUnavailable: "اکاؤنٹ سائن اِن دستیاب نہیں",
    accountSignInUnavailableHint: "بیک ٹیسٹ پھر بھی چل سکتے ہیں۔ اکاؤنٹ سنک عارضی طور پر دستیاب نہیں۔",
    downloadOnAppStore: "App Store سے ڈاؤن لوڈ کریں",
    emptyResultsTitle: "ابھی کوئی بیک ٹیسٹ نتیجہ نہیں",
    methodology: "طریقۂ کار",
    periodClampedByPlan: "مدت آپ کے پلان کی حد کے مطابق ایڈجسٹ کر دی گئی",
    loadingBacktestTitle: "مارکیٹ سیمولیشن چل رہی ہے",
    loadingBacktestData: "کینڈل ڈیٹا لوڈ ہو رہا ہے",
    loadingBacktestEngine: "حکمت عملی کے اصول جانچے جا رہے ہیں",
    loadingBacktestReport: "چارٹ اور ٹریڈ رپورٹ تیار ہو رہی ہے",
    loadingAnalysisTitle: "ٹریڈ کے رویّے کا جائزہ لیا جا رہا ہے",
    loadingAnalysisData: "بند ٹریڈز پڑھے جا رہے ہیں",
    loadingAnalysisEngine: "کمزور پہلو تلاش کیے جا رہے ہیں",
    loadingAnalysisReport: "بہتر پرامپٹ تیار ہو رہا ہے",
    estimatedWait: "متوقع انتظار",
    privacyTermsTitle: "استعمال کی شرائط",
    privacyTermsBody: "سبسکرپشن iOS ایپ میں خریدی جاتی ہے اور Apple کی معیاری شرائطِ استعمال کے تابع ہے۔ خریداری سے پہلے Apple حتمی مقامی قیمت اور تجدید کی شرائط دکھاتا ہے۔",
    privacyTermsLink: "Apple کی شرائطِ استعمال (EULA)",
    webSubscriptionTerms: "ویب سبسکرپشن کی شرائط",
    sample: "نمونہ",
    confidenceHigh: "زیادہ",
    confidenceMedium: "درمیانہ",
    confidenceLow: "کم",
    confidenceLargeTradeSample: "ٹریڈز کا بڑا نمونہ",
    confidenceModerateTradeSample: "ٹریڈز کا مناسب نمونہ",
    confidenceSmallTradeSample: "چھوٹا مگر قابلِ استعمال ٹریڈ نمونہ",
    confidenceVerySmallTradeSample: "ٹریڈز کا بہت چھوٹا نمونہ",
    confidenceWideCandleSample: "کینڈلز کا وسیع نمونہ",
    confidenceShortCandleHistory: "مختصر کینڈل ہسٹری",
    confidenceNoGrossLoss: "اس نمونے میں کوئی مجموعی خسارے والی ٹریڈ نہیں",
    confidenceNoClosedTrades: "کوئی بند ٹریڈ نہیں",
    confidenceStrongProfitFactor: "مضبوط پرافٹ فیکٹر",
    confidencePositiveProfitFactor: "مثبت پرافٹ فیکٹر",
    confidenceWeakProfitFactor: "پرافٹ فیکٹر 1.0 سے کم",
    confidencePositiveNetProfit: "اخراجات کے بعد مثبت خالص منافع",
    confidenceNegativeNetProfit: "اخراجات کے بعد منفی خالص منافع",
    confidenceHighWinRate: "کامیابی کی شرح 60% سے زیادہ",
    confidenceLowWinRate: "کامیابی کی شرح 40% سے کم",
    confidenceControlledDrawdown: "قابو میں ڈرا ڈاؤن",
    confidenceHighDrawdown: "زیادہ ڈرا ڈاؤن",
    confidenceModerateDrawdown: "درمیانہ ڈرا ڈاؤن",
    confidenceConcentratedProfit: "منافع ایک بڑی کامیاب ٹریڈ پر بہت زیادہ منحصر ہے",
    viewHistory: "ہسٹری دیکھیں",
    emailAlreadyInUse: "اس ای میل کے ساتھ اکاؤنٹ پہلے سے موجود ہے۔",
    invalidCredentials: "ای میل یا پاس ورڈ درست نہیں۔",
    passwordTooWeak: "پاس ورڈ کم از کم 8 حروف کا ہونا چاہیے۔",
    signInUnavailable: "سائن اِن عارضی طور پر دستیاب نہیں۔ براہِ کرم بعد میں دوبارہ کوشش کریں۔",
  },
  th: {
    accountSignInUnavailable: "ไม่สามารถลงชื่อเข้าใช้บัญชีได้",
    accountSignInUnavailableHint: "ยังรันแบ็กเทสต์ได้ แต่การซิงค์บัญชีไม่พร้อมใช้งานชั่วคราว",
    downloadOnAppStore: "ดาวน์โหลดบน App Store",
    emptyResultsTitle: "ยังไม่มีผลแบ็กเทสต์",
    methodology: "วิธีการคำนวณ",
    periodClampedByPlan: "ปรับช่วงเวลาให้ตรงกับขีดจำกัดของแพ็กเกจแล้ว",
    loadingBacktestTitle: "กำลังจำลองตลาด",
    loadingBacktestData: "กำลังโหลดข้อมูลแท่งราคา",
    loadingBacktestEngine: "กำลังทดสอบกฎของกลยุทธ์",
    loadingBacktestReport: "กำลังสร้างกราฟและรายงานการเทรด",
    loadingAnalysisTitle: "กำลังตรวจสอบพฤติกรรมการเทรด",
    loadingAnalysisData: "กำลังอ่านรายการเทรดที่ปิดแล้ว",
    loadingAnalysisEngine: "กำลังค้นหาจุดอ่อน",
    loadingAnalysisReport: "กำลังเตรียมพรอมต์ที่ปรับปรุงแล้ว",
    estimatedWait: "เวลารอโดยประมาณ",
    privacyTermsTitle: "ข้อกำหนดการใช้งาน",
    privacyTermsBody: "การสมัครสมาชิกซื้อผ่านแอป iOS และอยู่ภายใต้ข้อกำหนดการใช้งานมาตรฐานของ Apple โดย Apple จะแสดงราคาท้องถิ่นสุดท้ายและเงื่อนไขการต่ออายุก่อนซื้อ",
    privacyTermsLink: "ข้อกำหนดการใช้งานของ Apple (EULA)",
    webSubscriptionTerms: "ข้อกำหนดการสมัครสมาชิกบนเว็บ",
    sample: "ตัวอย่าง",
    confidenceHigh: "สูง",
    confidenceMedium: "ปานกลาง",
    confidenceLow: "ต่ำ",
    confidenceLargeTradeSample: "ตัวอย่างการเทรดขนาดใหญ่",
    confidenceModerateTradeSample: "ตัวอย่างการเทรดขนาดปานกลาง",
    confidenceSmallTradeSample: "ตัวอย่างการเทรดขนาดเล็กแต่ยังใช้งานได้",
    confidenceVerySmallTradeSample: "ตัวอย่างการเทรดน้อยมาก",
    confidenceWideCandleSample: "ตัวอย่างแท่งราคาครอบคลุมกว้าง",
    confidenceShortCandleHistory: "ประวัติแท่งราคาสั้น",
    confidenceNoGrossLoss: "ไม่มีเทรดที่ขาดทุนขั้นต้นในตัวอย่างนี้",
    confidenceNoClosedTrades: "ไม่มีเทรดที่ปิดแล้ว",
    confidenceStrongProfitFactor: "Profit factor แข็งแกร่ง",
    confidencePositiveProfitFactor: "Profit factor เป็นบวก",
    confidenceWeakProfitFactor: "Profit factor ต่ำกว่า 1.0",
    confidencePositiveNetProfit: "กำไรสุทธิหลังหักต้นทุนเป็นบวก",
    confidenceNegativeNetProfit: "กำไรสุทธิหลังหักต้นทุนเป็นลบ",
    confidenceHighWinRate: "อัตราชนะสูงกว่า 60%",
    confidenceLowWinRate: "อัตราชนะต่ำกว่า 40%",
    confidenceControlledDrawdown: "Drawdown อยู่ในระดับควบคุม",
    confidenceHighDrawdown: "Drawdown สูง",
    confidenceModerateDrawdown: "Drawdown ปานกลาง",
    confidenceConcentratedProfit: "กำไรพึ่งพาเทรดที่ชนะครั้งใหญ่มากเกินไป",
    viewHistory: "ดูประวัติ",
    emailAlreadyInUse: "มีบัญชีที่ใช้อีเมลนี้อยู่แล้ว",
    invalidCredentials: "อีเมลหรือรหัสผ่านไม่ถูกต้อง",
    passwordTooWeak: "รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร",
    signInUnavailable: "ไม่สามารถลงชื่อเข้าใช้ได้ชั่วคราว โปรดลองอีกครั้งภายหลัง",
  },
  bn: {
    accountSignInUnavailable: "অ্যাকাউন্টে সাইন ইন করা যাচ্ছে না",
    accountSignInUnavailableHint: "ব্যাকটেস্ট চালানো যাবে। অ্যাকাউন্ট সিঙ্ক সাময়িকভাবে অনুপলব্ধ।",
    downloadOnAppStore: "App Store থেকে ডাউনলোড করুন",
    emptyResultsTitle: "এখনও কোনো ব্যাকটেস্ট ফলাফল নেই",
    methodology: "পদ্ধতি",
    periodClampedByPlan: "সময়সীমা আপনার প্ল্যানের সীমা অনুযায়ী সমন্বয় করা হয়েছে",
    loadingBacktestTitle: "বাজার সিমুলেশন চলছে",
    loadingBacktestData: "ক্যান্ডেল ডেটা লোড হচ্ছে",
    loadingBacktestEngine: "কৌশলের নিয়ম পরীক্ষা হচ্ছে",
    loadingBacktestReport: "চার্ট ও ট্রেড রিপোর্ট তৈরি হচ্ছে",
    loadingAnalysisTitle: "ট্রেডের আচরণ পর্যালোচনা হচ্ছে",
    loadingAnalysisData: "বন্ধ ট্রেড পড়া হচ্ছে",
    loadingAnalysisEngine: "দুর্বল দিক খোঁজা হচ্ছে",
    loadingAnalysisReport: "উন্নত প্রম্পট প্রস্তুত হচ্ছে",
    estimatedWait: "আনুমানিক অপেক্ষা",
    privacyTermsTitle: "ব্যবহারের শর্তাবলি",
    privacyTermsBody: "সাবস্ক্রিপশন iOS অ্যাপে কেনা হয় এবং Apple-এর মানক ব্যবহারের শর্তাবলির অধীন। কেনার আগে Apple চূড়ান্ত স্থানীয় মূল্য ও নবায়নের শর্ত দেখায়।",
    privacyTermsLink: "Apple-এর ব্যবহারের শর্তাবলি (EULA)",
    webSubscriptionTerms: "ওয়েব সাবস্ক্রিপশনের শর্তাবলি",
    sample: "নমুনা",
    confidenceHigh: "উচ্চ",
    confidenceMedium: "মাঝারি",
    confidenceLow: "কম",
    confidenceLargeTradeSample: "বড় ট্রেড নমুনা",
    confidenceModerateTradeSample: "মাঝারি ট্রেড নমুনা",
    confidenceSmallTradeSample: "ছোট কিন্তু ব্যবহারযোগ্য ট্রেড নমুনা",
    confidenceVerySmallTradeSample: "খুব ছোট ট্রেড নমুনা",
    confidenceWideCandleSample: "বিস্তৃত ক্যান্ডেল নমুনা",
    confidenceShortCandleHistory: "স্বল্প ক্যান্ডেল ইতিহাস",
    confidenceNoGrossLoss: "এই নমুনায় কোনো মোট লোকসানের ট্রেড নেই",
    confidenceNoClosedTrades: "কোনো বন্ধ ট্রেড নেই",
    confidenceStrongProfitFactor: "শক্তিশালী প্রফিট ফ্যাক্টর",
    confidencePositiveProfitFactor: "ইতিবাচক প্রফিট ফ্যাক্টর",
    confidenceWeakProfitFactor: "প্রফিট ফ্যাক্টর 1.0-এর নিচে",
    confidencePositiveNetProfit: "খরচের পর নিট মুনাফা ইতিবাচক",
    confidenceNegativeNetProfit: "খরচের পর নিট মুনাফা নেতিবাচক",
    confidenceHighWinRate: "জয়ের হার 60%-এর বেশি",
    confidenceLowWinRate: "জয়ের হার 40%-এর কম",
    confidenceControlledDrawdown: "নিয়ন্ত্রিত ড্রডাউন",
    confidenceHighDrawdown: "উচ্চ ড্রডাউন",
    confidenceModerateDrawdown: "মাঝারি ড্রডাউন",
    confidenceConcentratedProfit: "মুনাফা একটি বড় জয়ী ট্রেডের ওপর অতিরিক্ত নির্ভরশীল",
    viewHistory: "ইতিহাস দেখুন",
    emailAlreadyInUse: "এই ইমেইল দিয়ে ইতিমধ্যে একটি অ্যাকাউন্ট রয়েছে।",
    invalidCredentials: "ইমেইল বা পাসওয়ার্ড সঠিক নয়।",
    passwordTooWeak: "পাসওয়ার্ড অন্তত 8 অক্ষরের হতে হবে।",
    signInUnavailable: "সাইন ইন সাময়িকভাবে অনুপলব্ধ। পরে আবার চেষ্টা করুন।",
  },
  id: {
    accountSignInUnavailable: "Login akun tidak tersedia",
    accountSignInUnavailableHint: "Backtest tetap dapat dijalankan. Sinkronisasi akun sementara tidak tersedia.",
    downloadOnAppStore: "Unduh di App Store",
    emptyResultsTitle: "Belum ada hasil backtest",
    methodology: "Metodologi",
    periodClampedByPlan: "Rentang disesuaikan dengan batas paket Anda",
    loadingBacktestTitle: "Menjalankan simulasi pasar",
    loadingBacktestData: "Memuat data candle",
    loadingBacktestEngine: "Menguji aturan strategi",
    loadingBacktestReport: "Menyusun grafik dan laporan trade",
    loadingAnalysisTitle: "Meninjau perilaku trade",
    loadingAnalysisData: "Membaca trade yang sudah ditutup",
    loadingAnalysisEngine: "Mencari kelemahan",
    loadingAnalysisReport: "Menyiapkan prompt yang ditingkatkan",
    estimatedWait: "Perkiraan waktu tunggu",
    privacyTermsTitle: "Ketentuan Penggunaan",
    privacyTermsBody: "Langganan dibeli di aplikasi iOS dan tunduk pada Ketentuan Penggunaan standar Apple. Apple menampilkan harga lokal final dan ketentuan perpanjangan sebelum pembelian.",
    privacyTermsLink: "Ketentuan Penggunaan Apple (EULA)",
    webSubscriptionTerms: "Ketentuan Langganan Web",
    sample: "Sampel",
    confidenceHigh: "Tinggi",
    confidenceMedium: "Sedang",
    confidenceLow: "Rendah",
    confidenceLargeTradeSample: "Sampel trade besar",
    confidenceModerateTradeSample: "Sampel trade sedang",
    confidenceSmallTradeSample: "Sampel trade kecil tetapi masih dapat digunakan",
    confidenceVerySmallTradeSample: "Sampel trade sangat kecil",
    confidenceWideCandleSample: "Sampel candle luas",
    confidenceShortCandleHistory: "Riwayat candle pendek",
    confidenceNoGrossLoss: "Tidak ada trade rugi kotor dalam sampel ini",
    confidenceNoClosedTrades: "Tidak ada trade yang ditutup",
    confidenceStrongProfitFactor: "Profit factor kuat",
    confidencePositiveProfitFactor: "Profit factor positif",
    confidenceWeakProfitFactor: "Profit factor di bawah 1,0",
    confidencePositiveNetProfit: "Laba bersih setelah biaya positif",
    confidenceNegativeNetProfit: "Laba bersih setelah biaya negatif",
    confidenceHighWinRate: "Win rate di atas 60%",
    confidenceLowWinRate: "Win rate di bawah 40%",
    confidenceControlledDrawdown: "Drawdown terkendali",
    confidenceHighDrawdown: "Drawdown tinggi",
    confidenceModerateDrawdown: "Drawdown sedang",
    confidenceConcentratedProfit: "Profit terlalu bergantung pada satu trade pemenang besar",
    viewHistory: "Lihat riwayat",
    emailAlreadyInUse: "Akun dengan email ini sudah ada.",
    invalidCredentials: "Email atau kata sandi salah.",
    passwordTooWeak: "Kata sandi harus terdiri dari minimal 8 karakter.",
    signInUnavailable: "Login sementara tidak tersedia. Silakan coba lagi nanti.",
  },
  fil: {
    accountSignInUnavailable: "Hindi available ang pag-sign in sa account",
    accountSignInUnavailableHint: "Maaari pa ring magpatakbo ng backtest. Pansamantalang hindi available ang account sync.",
    downloadOnAppStore: "I-download sa App Store",
    emptyResultsTitle: "Wala pang resulta ng backtest",
    methodology: "Pamamaraan",
    periodClampedByPlan: "Inayos ang range ayon sa limitasyon ng iyong plan",
    loadingBacktestTitle: "Pinapatakbo ang market simulation",
    loadingBacktestData: "Nilo-load ang candle data",
    loadingBacktestEngine: "Sinusuri ang mga tuntunin ng strategy",
    loadingBacktestReport: "Binubuo ang chart at trade report",
    loadingAnalysisTitle: "Sinusuri ang kilos ng mga trade",
    loadingAnalysisData: "Binabasa ang mga saradong trade",
    loadingAnalysisEngine: "Hinahanap ang mahihinang bahagi",
    loadingAnalysisReport: "Inihahanda ang pinahusay na prompt",
    estimatedWait: "Tinatayang paghihintay",
    privacyTermsTitle: "Mga Tuntunin ng Paggamit",
    privacyTermsBody: "Binibili ang mga subscription sa iOS app at sakop ng karaniwang Mga Tuntunin ng Paggamit ng Apple. Ipinapakita ng Apple ang huling lokal na presyo at mga tuntunin ng renewal bago bumili.",
    privacyTermsLink: "Mga Tuntunin ng Paggamit ng Apple (EULA)",
    webSubscriptionTerms: "Mga Tuntunin ng Web Subscription",
    sample: "Sample",
    confidenceHigh: "Mataas",
    confidenceMedium: "Katamtaman",
    confidenceLow: "Mababa",
    confidenceLargeTradeSample: "Malaking sample ng trade",
    confidenceModerateTradeSample: "Katamtamang sample ng trade",
    confidenceSmallTradeSample: "Maliit ngunit magagamit na sample ng trade",
    confidenceVerySmallTradeSample: "Napakaliit na sample ng trade",
    confidenceWideCandleSample: "Malawak na sample ng candle",
    confidenceShortCandleHistory: "Maikling kasaysayan ng candle",
    confidenceNoGrossLoss: "Walang trade na may gross loss sa sample na ito",
    confidenceNoClosedTrades: "Walang saradong trade",
    confidenceStrongProfitFactor: "Malakas na profit factor",
    confidencePositiveProfitFactor: "Positibong profit factor",
    confidenceWeakProfitFactor: "Mas mababa sa 1.0 ang profit factor",
    confidencePositiveNetProfit: "Positibo ang net profit pagkatapos ng gastos",
    confidenceNegativeNetProfit: "Negatibo ang net profit pagkatapos ng gastos",
    confidenceHighWinRate: "Higit sa 60% ang win rate",
    confidenceLowWinRate: "Mas mababa sa 40% ang win rate",
    confidenceControlledDrawdown: "Kontroladong drawdown",
    confidenceHighDrawdown: "Mataas na drawdown",
    confidenceModerateDrawdown: "Katamtamang drawdown",
    confidenceConcentratedProfit: "Masyadong nakadepende ang profit sa isang malaking panalong trade",
    viewHistory: "Tingnan ang history",
    emailAlreadyInUse: "May account na gamit ang email na ito.",
    invalidCredentials: "Mali ang email o password.",
    passwordTooWeak: "Dapat hindi bababa sa 8 character ang password.",
    signInUnavailable: "Pansamantalang hindi available ang pag-sign in. Subukan ulit mamaya.",
  },
  "es-419": {
    accountSignInUnavailable: "El inicio de sesión no está disponible",
    accountSignInUnavailableHint: "Los backtests aún pueden ejecutarse. La sincronización de la cuenta no está disponible temporalmente.",
    downloadOnAppStore: "Descargar en App Store",
    emptyResultsTitle: "Todavía no hay resultados de backtest",
    methodology: "Metodología",
    periodClampedByPlan: "El período se ajustó al límite de tu plan",
    loadingBacktestTitle: "Ejecutando la simulación del mercado",
    loadingBacktestData: "Cargando datos de velas",
    loadingBacktestEngine: "Probando las reglas de la estrategia",
    loadingBacktestReport: "Creando el gráfico y el reporte de operaciones",
    loadingAnalysisTitle: "Revisando el comportamiento de las operaciones",
    loadingAnalysisData: "Leyendo las operaciones cerradas",
    loadingAnalysisEngine: "Buscando puntos débiles",
    loadingAnalysisReport: "Preparando el prompt mejorado",
    estimatedWait: "Espera estimada",
    privacyTermsTitle: "Términos de uso",
    privacyTermsBody: "Las suscripciones se compran en la app para iOS y se rigen por los Términos de uso estándar de Apple. Apple muestra el precio local final y las condiciones de renovación antes de la compra.",
    privacyTermsLink: "Términos de uso de Apple (EULA)",
    webSubscriptionTerms: "Términos de suscripción web",
    sample: "Muestra",
    confidenceHigh: "Alta",
    confidenceMedium: "Media",
    confidenceLow: "Baja",
    confidenceLargeTradeSample: "Muestra grande de operaciones",
    confidenceModerateTradeSample: "Muestra moderada de operaciones",
    confidenceSmallTradeSample: "Muestra pequeña pero utilizable",
    confidenceVerySmallTradeSample: "Muestra muy pequeña de operaciones",
    confidenceWideCandleSample: "Muestra amplia de velas",
    confidenceShortCandleHistory: "Historial corto de velas",
    confidenceNoGrossLoss: "No hay operaciones con pérdida bruta en esta muestra",
    confidenceNoClosedTrades: "No hay operaciones cerradas",
    confidenceStrongProfitFactor: "Factor de ganancia sólido",
    confidencePositiveProfitFactor: "Factor de ganancia positivo",
    confidenceWeakProfitFactor: "Factor de ganancia inferior a 1.0",
    confidencePositiveNetProfit: "Ganancia neta positiva después de costos",
    confidenceNegativeNetProfit: "Ganancia neta negativa después de costos",
    confidenceHighWinRate: "Tasa de aciertos superior al 60%",
    confidenceLowWinRate: "Tasa de aciertos inferior al 40%",
    confidenceControlledDrawdown: "Drawdown controlado",
    confidenceHighDrawdown: "Drawdown alto",
    confidenceModerateDrawdown: "Drawdown moderado",
    confidenceConcentratedProfit: "La ganancia depende demasiado de una sola operación ganadora",
    viewHistory: "Ver historial",
    emailAlreadyInUse: "Ya existe una cuenta con este correo electrónico.",
    invalidCredentials: "El correo electrónico o la contraseña son incorrectos.",
    passwordTooWeak: "La contraseña debe tener al menos 8 caracteres.",
    signInUnavailable: "El inicio de sesión no está disponible temporalmente. Inténtalo de nuevo más tarde.",
  },
  "pt-BR": {
    accountSignInUnavailable: "O login da conta não está disponível",
    accountSignInUnavailableHint: "Os backtests ainda podem ser executados. A sincronização da conta está temporariamente indisponível.",
    downloadOnAppStore: "Baixar na App Store",
    emptyResultsTitle: "Ainda não há resultado de backtest",
    methodology: "Metodologia",
    periodClampedByPlan: "O período foi ajustado ao limite do seu plano",
    loadingBacktestTitle: "Executando a simulação de mercado",
    loadingBacktestData: "Carregando dados de candles",
    loadingBacktestEngine: "Testando as regras da estratégia",
    loadingBacktestReport: "Montando o gráfico e o relatório de trades",
    loadingAnalysisTitle: "Analisando o comportamento dos trades",
    loadingAnalysisData: "Lendo os trades encerrados",
    loadingAnalysisEngine: "Procurando pontos fracos",
    loadingAnalysisReport: "Preparando o prompt aprimorado",
    estimatedWait: "Espera estimada",
    privacyTermsTitle: "Termos de Uso",
    privacyTermsBody: "As assinaturas são compradas no app para iOS e seguem os Termos de Uso padrão da Apple. A Apple mostra o preço local final e as condições de renovação antes da compra.",
    privacyTermsLink: "Termos de Uso da Apple (EULA)",
    webSubscriptionTerms: "Termos da Assinatura Web",
    sample: "Amostra",
    confidenceHigh: "Alta",
    confidenceMedium: "Média",
    confidenceLow: "Baixa",
    confidenceLargeTradeSample: "Amostra grande de trades",
    confidenceModerateTradeSample: "Amostra moderada de trades",
    confidenceSmallTradeSample: "Amostra pequena, mas utilizável",
    confidenceVerySmallTradeSample: "Amostra muito pequena de trades",
    confidenceWideCandleSample: "Amostra ampla de candles",
    confidenceShortCandleHistory: "Histórico curto de candles",
    confidenceNoGrossLoss: "Nenhum trade com perda bruta nesta amostra",
    confidenceNoClosedTrades: "Nenhum trade encerrado",
    confidenceStrongProfitFactor: "Fator de lucro forte",
    confidencePositiveProfitFactor: "Fator de lucro positivo",
    confidenceWeakProfitFactor: "Fator de lucro abaixo de 1,0",
    confidencePositiveNetProfit: "Lucro líquido positivo após os custos",
    confidenceNegativeNetProfit: "Lucro líquido negativo após os custos",
    confidenceHighWinRate: "Taxa de acerto acima de 60%",
    confidenceLowWinRate: "Taxa de acerto abaixo de 40%",
    confidenceControlledDrawdown: "Drawdown controlado",
    confidenceHighDrawdown: "Drawdown alto",
    confidenceModerateDrawdown: "Drawdown moderado",
    confidenceConcentratedProfit: "O lucro depende demais de um único trade vencedor",
    viewHistory: "Ver histórico",
    emailAlreadyInUse: "Já existe uma conta com este e-mail.",
    invalidCredentials: "O e-mail ou a senha estão incorretos.",
    passwordTooWeak: "A senha deve ter pelo menos 8 caracteres.",
    signInUnavailable: "O login está temporariamente indisponível. Tente novamente mais tarde.",
  },
  ja: {
    accountSignInUnavailable: "アカウントにサインインできません",
    accountSignInUnavailableHint: "バックテストは引き続き実行できます。アカウント同期は一時的に利用できません。",
    downloadOnAppStore: "App Storeからダウンロード",
    emptyResultsTitle: "バックテスト結果はまだありません",
    methodology: "計算方法",
    periodClampedByPlan: "期間をプランの上限に合わせて調整しました",
    loadingBacktestTitle: "市場シミュレーションを実行中",
    loadingBacktestData: "ローソク足データを読み込み中",
    loadingBacktestEngine: "戦略ルールを検証中",
    loadingBacktestReport: "チャートと取引レポートを作成中",
    loadingAnalysisTitle: "取引動向を分析中",
    loadingAnalysisData: "決済済み取引を読み込み中",
    loadingAnalysisEngine: "弱点を検出中",
    loadingAnalysisReport: "改善プロンプトを作成中",
    estimatedWait: "予想待ち時間",
    privacyTermsTitle: "利用規約",
    privacyTermsBody: "サブスクリプションはiOSアプリ内で購入し、Appleの標準利用規約が適用されます。購入前にAppleが最終的な現地価格と更新条件を表示します。",
    privacyTermsLink: "Apple利用規約（EULA）",
    webSubscriptionTerms: "ウェブサブスクリプション規約",
    sample: "サンプル",
    confidenceHigh: "高",
    confidenceMedium: "中",
    confidenceLow: "低",
    confidenceLargeTradeSample: "十分に大きい取引サンプル",
    confidenceModerateTradeSample: "中程度の取引サンプル",
    confidenceSmallTradeSample: "小さいが利用可能な取引サンプル",
    confidenceVerySmallTradeSample: "取引サンプルが非常に少ない",
    confidenceWideCandleSample: "広範なローソク足サンプル",
    confidenceShortCandleHistory: "ローソク足の履歴が短い",
    confidenceNoGrossLoss: "このサンプルには総損失となる取引がありません",
    confidenceNoClosedTrades: "決済済み取引なし",
    confidenceStrongProfitFactor: "高いプロフィットファクター",
    confidencePositiveProfitFactor: "プラスのプロフィットファクター",
    confidenceWeakProfitFactor: "プロフィットファクターが1.0未満",
    confidencePositiveNetProfit: "コスト控除後の純利益がプラス",
    confidenceNegativeNetProfit: "コスト控除後の純利益がマイナス",
    confidenceHighWinRate: "勝率が60%超",
    confidenceLowWinRate: "勝率が40%未満",
    confidenceControlledDrawdown: "ドローダウンは制御範囲内",
    confidenceHighDrawdown: "ドローダウンが大きい",
    confidenceModerateDrawdown: "ドローダウンは中程度",
    confidenceConcentratedProfit: "利益が1回の大きな勝ち取引に偏っています",
    viewHistory: "履歴を見る",
    emailAlreadyInUse: "このメールアドレスのアカウントはすでに存在します。",
    invalidCredentials: "メールアドレスまたはパスワードが正しくありません。",
    passwordTooWeak: "パスワードは8文字以上にしてください。",
    signInUnavailable: "現在サインインできません。しばらくしてからもう一度お試しください。",
  },
  ko: {
    accountSignInUnavailable: "계정 로그인 사용 불가",
    accountSignInUnavailableHint: "백테스트는 계속 실행할 수 있습니다. 계정 동기화는 일시적으로 사용할 수 없습니다.",
    downloadOnAppStore: "App Store에서 다운로드",
    emptyResultsTitle: "아직 백테스트 결과가 없습니다",
    methodology: "계산 방법",
    periodClampedByPlan: "기간을 플랜 한도에 맞게 조정했습니다",
    loadingBacktestTitle: "시장 시뮬레이션 실행 중",
    loadingBacktestData: "캔들 데이터 불러오는 중",
    loadingBacktestEngine: "전략 규칙 테스트 중",
    loadingBacktestReport: "차트와 거래 보고서 생성 중",
    loadingAnalysisTitle: "거래 행동 검토 중",
    loadingAnalysisData: "종료된 거래 읽는 중",
    loadingAnalysisEngine: "취약점 찾는 중",
    loadingAnalysisReport: "개선된 프롬프트 준비 중",
    estimatedWait: "예상 대기 시간",
    privacyTermsTitle: "이용 약관",
    privacyTermsBody: "구독은 iOS 앱에서 구매하며 Apple의 표준 이용 약관이 적용됩니다. 구매 전에 Apple이 최종 현지 가격과 갱신 조건을 안내합니다.",
    privacyTermsLink: "Apple 이용 약관(EULA)",
    webSubscriptionTerms: "웹 구독 약관",
    sample: "표본",
    confidenceHigh: "높음",
    confidenceMedium: "보통",
    confidenceLow: "낮음",
    confidenceLargeTradeSample: "큰 거래 표본",
    confidenceModerateTradeSample: "중간 규모 거래 표본",
    confidenceSmallTradeSample: "작지만 사용 가능한 거래 표본",
    confidenceVerySmallTradeSample: "매우 작은 거래 표본",
    confidenceWideCandleSample: "넓은 캔들 표본",
    confidenceShortCandleHistory: "짧은 캔들 기록",
    confidenceNoGrossLoss: "이 표본에는 총손실 거래가 없음",
    confidenceNoClosedTrades: "종료된 거래 없음",
    confidenceStrongProfitFactor: "높은 수익 계수",
    confidencePositiveProfitFactor: "양의 수익 계수",
    confidenceWeakProfitFactor: "수익 계수가 1.0 미만",
    confidencePositiveNetProfit: "비용 차감 후 순이익이 양수",
    confidenceNegativeNetProfit: "비용 차감 후 순이익이 음수",
    confidenceHighWinRate: "승률 60% 초과",
    confidenceLowWinRate: "승률 40% 미만",
    confidenceControlledDrawdown: "통제된 낙폭",
    confidenceHighDrawdown: "큰 낙폭",
    confidenceModerateDrawdown: "중간 낙폭",
    confidenceConcentratedProfit: "수익이 한 번의 큰 수익 거래에 지나치게 의존함",
    viewHistory: "기록 보기",
    emailAlreadyInUse: "이 이메일로 가입한 계정이 이미 있습니다.",
    invalidCredentials: "이메일 또는 비밀번호가 올바르지 않습니다.",
    passwordTooWeak: "비밀번호는 8자 이상이어야 합니다.",
    signInUnavailable: "로그인을 일시적으로 사용할 수 없습니다. 나중에 다시 시도하세요.",
  },
  ru: {
    accountSignInUnavailable: "Вход в аккаунт недоступен",
    accountSignInUnavailableHint: "Бэктесты по-прежнему доступны. Синхронизация аккаунта временно не работает.",
    downloadOnAppStore: "Загрузить в App Store",
    emptyResultsTitle: "Результатов бэктеста пока нет",
    methodology: "Методология",
    periodClampedByPlan: "Период скорректирован с учётом лимита тарифа",
    loadingBacktestTitle: "Выполняется симуляция рынка",
    loadingBacktestData: "Загружаются данные свечей",
    loadingBacktestEngine: "Проверяются правила стратегии",
    loadingBacktestReport: "Формируются график и отчёт по сделкам",
    loadingAnalysisTitle: "Анализируется поведение сделок",
    loadingAnalysisData: "Читаются закрытые сделки",
    loadingAnalysisEngine: "Выявляются слабые места",
    loadingAnalysisReport: "Готовится улучшенный запрос",
    estimatedWait: "Ожидаемое время",
    privacyTermsTitle: "Условия использования",
    privacyTermsBody: "Подписки приобретаются в приложении iOS и регулируются стандартными Условиями использования Apple. Перед покупкой Apple показывает итоговую местную цену и условия продления.",
    privacyTermsLink: "Условия использования Apple (EULA)",
    webSubscriptionTerms: "Условия веб-подписки",
    sample: "Выборка",
    confidenceHigh: "Высокая",
    confidenceMedium: "Средняя",
    confidenceLow: "Низкая",
    confidenceLargeTradeSample: "Большая выборка сделок",
    confidenceModerateTradeSample: "Средняя выборка сделок",
    confidenceSmallTradeSample: "Небольшая, но пригодная выборка сделок",
    confidenceVerySmallTradeSample: "Очень маленькая выборка сделок",
    confidenceWideCandleSample: "Широкая выборка свечей",
    confidenceShortCandleHistory: "Короткая история свечей",
    confidenceNoGrossLoss: "В этой выборке нет сделок с валовым убытком",
    confidenceNoClosedTrades: "Нет закрытых сделок",
    confidenceStrongProfitFactor: "Высокий профит-фактор",
    confidencePositiveProfitFactor: "Положительный профит-фактор",
    confidenceWeakProfitFactor: "Профит-фактор ниже 1,0",
    confidencePositiveNetProfit: "Положительная чистая прибыль после издержек",
    confidenceNegativeNetProfit: "Отрицательная чистая прибыль после издержек",
    confidenceHighWinRate: "Доля прибыльных сделок выше 60%",
    confidenceLowWinRate: "Доля прибыльных сделок ниже 40%",
    confidenceControlledDrawdown: "Контролируемая просадка",
    confidenceHighDrawdown: "Высокая просадка",
    confidenceModerateDrawdown: "Умеренная просадка",
    confidenceConcentratedProfit: "Прибыль слишком сильно зависит от одной крупной выигрышной сделки",
    viewHistory: "Посмотреть историю",
    emailAlreadyInUse: "Аккаунт с этим адресом электронной почты уже существует.",
    invalidCredentials: "Неверный адрес электронной почты или пароль.",
    passwordTooWeak: "Пароль должен содержать не менее 8 символов.",
    signInUnavailable: "Вход временно недоступен. Повторите попытку позже.",
  },
  tr: {
    accountSignInUnavailable: "Hesap girişi kullanılamıyor",
    accountSignInUnavailableHint: "Backtest çalıştırmaya devam edebilirsiniz. Hesap eşitleme geçici olarak kullanılamıyor.",
    downloadOnAppStore: "App Store’dan indirin",
    emptyResultsTitle: "Henüz backtest sonucu yok",
    methodology: "Metodoloji",
    periodClampedByPlan: "Dönem, plan sınırınıza göre ayarlandı",
    loadingBacktestTitle: "Piyasa simülasyonu çalıştırılıyor",
    loadingBacktestData: "Mum verileri yükleniyor",
    loadingBacktestEngine: "Strateji kuralları test ediliyor",
    loadingBacktestReport: "Grafik ve işlem raporu hazırlanıyor",
    loadingAnalysisTitle: "İşlem davranışı inceleniyor",
    loadingAnalysisData: "Kapanan işlemler okunuyor",
    loadingAnalysisEngine: "Zayıf noktalar aranıyor",
    loadingAnalysisReport: "İyileştirilmiş istem hazırlanıyor",
    estimatedWait: "Tahmini bekleme",
    privacyTermsTitle: "Kullanım Koşulları",
    privacyTermsBody: "Abonelikler iOS uygulamasından satın alınır ve Apple’ın standart Kullanım Koşullarına tabidir. Apple, satın almadan önce nihai yerel fiyatı ve yenileme koşullarını gösterir.",
    privacyTermsLink: "Apple Kullanım Koşulları (EULA)",
    webSubscriptionTerms: "Web Abonelik Koşulları",
    sample: "Örneklem",
    confidenceHigh: "Yüksek",
    confidenceMedium: "Orta",
    confidenceLow: "Düşük",
    confidenceLargeTradeSample: "Büyük işlem örneklemi",
    confidenceModerateTradeSample: "Orta büyüklükte işlem örneklemi",
    confidenceSmallTradeSample: "Küçük ama kullanılabilir işlem örneklemi",
    confidenceVerySmallTradeSample: "Çok küçük işlem örneklemi",
    confidenceWideCandleSample: "Geniş mum örneklemi",
    confidenceShortCandleHistory: "Kısa mum geçmişi",
    confidenceNoGrossLoss: "Bu örneklemde brüt zararlı işlem yok",
    confidenceNoClosedTrades: "Kapanan işlem yok",
    confidenceStrongProfitFactor: "Güçlü kâr faktörü",
    confidencePositiveProfitFactor: "Pozitif kâr faktörü",
    confidenceWeakProfitFactor: "Kâr faktörü 1,0’ın altında",
    confidencePositiveNetProfit: "Maliyetlerden sonra pozitif net kâr",
    confidenceNegativeNetProfit: "Maliyetlerden sonra negatif net kâr",
    confidenceHighWinRate: "Kazanma oranı %60’ın üzerinde",
    confidenceLowWinRate: "Kazanma oranı %40’ın altında",
    confidenceControlledDrawdown: "Kontrollü düşüş",
    confidenceHighDrawdown: "Yüksek düşüş",
    confidenceModerateDrawdown: "Orta düzey düşüş",
    confidenceConcentratedProfit: "Kâr tek bir büyük kazanan işleme fazlasıyla bağlı",
    viewHistory: "Geçmişi görüntüle",
    emailAlreadyInUse: "Bu e-posta adresiyle bir hesap zaten var.",
    invalidCredentials: "E-posta veya parola yanlış.",
    passwordTooWeak: "Parola en az 8 karakter olmalıdır.",
    signInUnavailable: "Giriş geçici olarak kullanılamıyor. Lütfen daha sonra tekrar deneyin.",
  },
  uk: {
    accountSignInUnavailable: "Вхід в обліковий запис недоступний",
    accountSignInUnavailableHint: "Бектести й надалі доступні. Синхронізація облікового запису тимчасово не працює.",
    downloadOnAppStore: "Завантажити в App Store",
    emptyResultsTitle: "Результатів бектесту ще немає",
    methodology: "Методологія",
    periodClampedByPlan: "Період скориговано відповідно до ліміту плану",
    loadingBacktestTitle: "Виконується симуляція ринку",
    loadingBacktestData: "Завантажуються дані свічок",
    loadingBacktestEngine: "Перевіряються правила стратегії",
    loadingBacktestReport: "Формуються графік і звіт про угоди",
    loadingAnalysisTitle: "Аналізується поведінка угод",
    loadingAnalysisData: "Читаються закриті угоди",
    loadingAnalysisEngine: "Виявляються слабкі місця",
    loadingAnalysisReport: "Готується покращений запит",
    estimatedWait: "Очікуваний час",
    privacyTermsTitle: "Умови використання",
    privacyTermsBody: "Підписки купуються в застосунку iOS і регулюються стандартними Умовами використання Apple. Перед купівлею Apple показує остаточну місцеву ціну й умови поновлення.",
    privacyTermsLink: "Умови використання Apple (EULA)",
    webSubscriptionTerms: "Умови вебпідписки",
    sample: "Вибірка",
    confidenceHigh: "Висока",
    confidenceMedium: "Середня",
    confidenceLow: "Низька",
    confidenceLargeTradeSample: "Велика вибірка угод",
    confidenceModerateTradeSample: "Середня вибірка угод",
    confidenceSmallTradeSample: "Невелика, але придатна вибірка угод",
    confidenceVerySmallTradeSample: "Дуже мала вибірка угод",
    confidenceWideCandleSample: "Широка вибірка свічок",
    confidenceShortCandleHistory: "Коротка історія свічок",
    confidenceNoGrossLoss: "У цій вибірці немає угод із валовим збитком",
    confidenceNoClosedTrades: "Немає закритих угод",
    confidenceStrongProfitFactor: "Високий профіт-фактор",
    confidencePositiveProfitFactor: "Позитивний профіт-фактор",
    confidenceWeakProfitFactor: "Профіт-фактор нижче 1,0",
    confidencePositiveNetProfit: "Позитивний чистий прибуток після витрат",
    confidenceNegativeNetProfit: "Від’ємний чистий прибуток після витрат",
    confidenceHighWinRate: "Частка прибуткових угод понад 60%",
    confidenceLowWinRate: "Частка прибуткових угод менше 40%",
    confidenceControlledDrawdown: "Контрольована просадка",
    confidenceHighDrawdown: "Висока просадка",
    confidenceModerateDrawdown: "Помірна просадка",
    confidenceConcentratedProfit: "Прибуток надто залежить від однієї великої виграшної угоди",
    viewHistory: "Переглянути історію",
    emailAlreadyInUse: "Обліковий запис із цією електронною адресою вже існує.",
    invalidCredentials: "Електронна адреса або пароль неправильні.",
    passwordTooWeak: "Пароль має містити щонайменше 8 символів.",
    signInUnavailable: "Вхід тимчасово недоступний. Спробуйте ще раз пізніше.",
  },
} satisfies Record<CompletionLocale, CompletionStrings>;

export const completionTranslations: Partial<Record<Locale, Partial<Record<TranslationKey, string>>>> = {
  vi: { downloadOnAppStore: "Tải trên App Store" },
  ...completeTranslations,
};
